import { FC, useCallback, useState } from "react";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { ConfigService } from "../../../services/config/configService";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import useFetch from "../../../hooks/useFetch";
import { ConfigApiResponse } from "../../../type/entities/config-type copy";
import Page from "../../../layout/Page/Page";
import CustomTable from "../../../components/table/CustomTable";
import { ActionIcon, Tooltip } from "rizzui";
import { MdOutlineModeEditOutline } from "react-icons/md";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import ConfigFilters from "./ConfigFilters";
import { FormDrawerView } from "../../../components/forms/FormDrawer";
import ConfigForm from "../components/ConfigForm";
import { CONFIG_OPTIONS } from "../../../utils/mapping";


const columnOrder = [
    'id',
    'label',
    'value',
    'description',
    'createdAt',
    'updatedAt',
];

interface ConfigListProps {
    openFormDrawer: boolean;
    setOpenFormDrawer: React.Dispatch<React.SetStateAction<boolean>>;
    configSelected: any;
    setConfigSelected: React.Dispatch<React.SetStateAction<any>>;
}

const ConfigList: FC<ConfigListProps> = ({ openFormDrawer, setOpenFormDrawer, configSelected, setConfigSelected }) => {
    
    // STATES

    const [changingStatus, setChangingStatus] = useState<string[]>([]);
    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    //HOOKS

    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const service = new ConfigService();
    const dispatch = useDispatch<AppDispatch>();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();

    // FETCH DATA

    // ----------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtiene el listado de configuraciones.
     * 
     * @EN Gets the list of configurations.
     * 
     */
    // ----------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await service.listConfigs(filters);
        return response.getResponseData() as ConfigApiResponse[];
    }, [filters]))
    // ----------------------------------------------------------------------------------------------------------


    // FUNCTIONS

    // ----------------------------------------------------------------------------------------------------------
    /**
     * @ES Maneja la eliminación de una configuración existente
     * 
     * @EN Handles the deletion of an existing configuration.
     */
    // ----------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        setIsLoading(true);
        try {
            const response = (await service.deleteConfig(id)).getResponseData();
            if (response.success) {
                handleErrors(response);
                refetch();
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsLoading(false);
        }
    }
    // ----------------------------------------------------------------------------------------------------------


    // ----------------------------------------------------------------------------------------------------------
    /**
     * @ES Maneja la eliminación de multiples configuraciones existentes
     * 
     * @EN Handles the deletion of multiple existing configurations.
     * 
     */
    // ----------------------------------------------------------------------------------------------------------
    const handleMultiDelete = async (ids: string[]) => {
        try {
            const response = (await service.deleteMultiConfigs(ids)).getResponseData();
            if (response.success) {
                handleErrors(response);
                refetch();
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsLoading(false);
        }
    }
    // ----------------------------------------------------------------------------------------------------------

    // ----------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     */
    //-------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });
    //-------------------------------------------------------------------------------------------------------------


    // RENDER
    
    return (
        <Page container="fluid">
            {data !== undefined && (
                <CustomTable
                    id={'config-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    classNameTable='fs-table'
                    headerTableClassName='fs-table-header'
                    floatingHorizontalScrollbar
                    isLoading={loading}
                    isSortable
                    columnsNoSortables={[]}
                    columnsNotShown={[]}
                    overrideColumns={[
                        {
                            key: 'label',
                            columnNameKey: 'etiqueta',
                            render: (row: any) => {
                                return (
                                    <span className="text-gray-700 font-medium">
                                        {CONFIG_OPTIONS.find((g: any) => g.value === row.row.original.label)?.label}
                                    </span>
                                )
                            }
                        },
                        {
                            key: 'value',
                            columnNameKey: 'valor',
                            render: (row: any) => {
                                return (
                                    <span className="text-gray-700 font-medium">
                                        {row.row.original.value}
                                    </span>
                                )
                            }
                        },
                    ]}
                    actions={[
                        {
                            label: 'Edit',
                            permissions: { group: 'config', permission: 'edit' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={"Editar configuración"}
                                        placement="top"
                                        color="invert"
                                    >
                                        <ActionIcon
                                            onClick={() => {
                                                setConfigSelected(row);
                                                setOpenFormDrawer(true);
                                            }}
                                            as="span"
                                            size="sm"
                                            variant="outline"
                                            className="hover:!border-gray-900 hover:text-gray-700 bg-white"
                                        >
                                            <MdOutlineModeEditOutline className="h-4 w-4" />
                                        </ActionIcon>
                                    </Tooltip>
                                )
                            }
                        },
                        {
                            label: 'Delete',
                            permissions: { group: 'config', permission: 'delete' },
                            render: (row: any) => {
                                return (
                                    <Tooltip
                                        size="sm"
                                        content={'Eliminar'}
                                        placement="top"
                                        color="invert"
                                    >
                                        <div>
                                            <DeletePopover
                                                title={"Eliminar configuración"}
                                                description={`¿Estás seguro de eliminar la configuración?`}
                                                onDelete={() => handleDelete(row.id)}
                                            />
                                        </div>
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                    handleMultipleDelete={handleMultiDelete}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: (data as ConfigApiResponse) ? data.lastPage : 1,
                        totalCount: data?.totalRegisters,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                />
            )}

            <FilterDrawerView
                isOpen={openFilters}
                setOpenDrawer={setOpenFilters}
                drawerTitle={'Filtros Configuración'}
                onClear={() => resetFilters(50)}
            >
                <ConfigFilters
                    filters={filters}
                    updateFilters={updateFilters}
                />
            </FilterDrawerView>


            <FormDrawerView
                isOpen={openFormDrawer}
                setOpenDrawer={setOpenFormDrawer}
                subtitleText={
                    configSelected ? 'Editar configuración' : 'Crear configuración'
                }
                drawerTitle={
                    configSelected ? 'Editar configuración' : 'Crear configuración'
                }
                submitText={configSelected ? 'Editar' : 'Crear'}
            >
                <ConfigForm
                    setOpenModal={setOpenFormDrawer}
                    configSelected={configSelected}
                    refetch={refetch}
                />
            </FormDrawerView>


        </Page>
    )
}

export default ConfigList;

